// The resize gesture's rules (05f) — main.js's 8-direction resize (Ben's #173) and its
// sticky handle dominance, the pure parts. Unit-tested; useModuleResize.ts and
// resize-dominance.ts apply them to the page.

import { GRID_GAP, pixelSize } from "./module-shell.js";
import { STEP } from "./placement.js";

/** Which edge(s) a handle drags. An 'n' or 'w' edge moves the anchor (left / top), since
 *  the opposite edge has to stay put. */
export type ResizeDir = "n" | "s" | "e" | "w" | "ne" | "nw" | "se" | "sw";

/** The body class that sets the cursor while resizing (#185's arrows). */
export const RESIZE_CURSOR_CLASS: Record<ResizeDir, "ns" | "ew" | "nesw" | "nwse"> = {
  n: "ns",
  s: "ns",
  e: "ew",
  w: "ew",
  ne: "nesw",
  sw: "nesw",
  nw: "nwse",
  se: "nwse",
};

export interface PxBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * The outline while resizing: it follows the pointer exactly, never smaller than one
 * block. For 'w' / 'n' the left / top come from the CLAMPED size (not the raw pointer
 * travel), so once the module hits its minimum it doesn't slide past its own edge.
 * `dx` / `dy` are already divided by the zoom.
 */
export function resizeOutline(dir: ResizeDir, start: PxBox, dx: number, dy: number): PxBox {
  const minSize = pixelSize(1);
  const out = { ...start };
  if (dir.includes("e")) out.width = Math.max(minSize, start.width + dx);
  if (dir.includes("w")) {
    out.width = Math.max(minSize, start.width - dx);
    out.left = start.left + (start.width - out.width);
  }
  if (dir.includes("s")) out.height = Math.max(minSize, start.height + dy);
  if (dir.includes("n")) {
    out.height = Math.max(minSize, start.height - dy);
    out.top = start.top + (start.height - out.height);
  }
  return out;
}

/** A pixel length → whole blocks (at least 1) — what the outline snaps to. */
export function blocksFor(length: number): number {
  return Math.max(1, Math.round((length + GRID_GAP) / STEP));
}

/** A module's box on screen, for the dominance pick. */
export interface ScreenRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

/** How far outside a module its resize handles reach (a rough halo — it doesn't need to
 *  match each handle's exact overhang). */
export const RESIZE_ZONE_MARGIN = 30;

/**
 * Which module's handles show (`resize-active`) — main.js updateResizeDominance, from
 * Bento's feature/resize-push (60802ba). Two modules' handle zones can overlap; plain CSS
 * :hover would then pick one arbitrarily. Instead the module you were already on keeps
 * its handles while the pointer is anywhere in its zone; otherwise one you're INSIDE
 * wins; otherwise the nearest centre. -1 = none.
 */
export function pickResizeDominant(rects: ScreenRect[], stickyIndex: number, x: number, y: number): number {
  const m = RESIZE_ZONE_MARGIN;
  const candidates = rects.flatMap((r, i) =>
    x >= r.left - m && x <= r.right + m && y >= r.top - m && y <= r.bottom + m ? [i] : [],
  );
  if (!candidates.length) return -1;
  if (candidates.includes(stickyIndex)) return stickyIndex;

  const inside = candidates.find((i) => {
    const r = rects[i]!;
    return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
  });
  if (inside !== undefined) return inside;

  let closest = candidates[0]!;
  let closestDist = Infinity;
  for (const i of candidates) {
    const r = rects[i]!;
    const dist = Math.hypot(x - (r.left + r.right) / 2, y - (r.top + r.bottom) / 2);
    // Strictly nearer: a tie keeps the first, as main.js's loop did.
    if (dist < closestDist) {
      closestDist = dist;
      closest = i;
    }
  }
  return closest;
}
